import { useCallback, useRef, useEffect } from 'react';
import { apiClient } from '../utils/api';

// Text-to-speech hook
export const useTextToSpeech = () => {
  const currentAudio = useRef<HTMLAudioElement | null>( null);
  const isSpeaking = useRef(false);

  // Stop all current audio
  const stopAllAudio = useCallback(() => {
    if (currentAudio.current) {
      currentAudio.current.pause();
      currentAudio.current.currentTime = 0;
      currentAudio.current = null;
    }

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    isSpeaking.current = false;
  }, []);

  // Fallback browser TTS
  const fallbackSpeak = useCallback((text: string) => {
    if (!('speechSynthesis' in window)) return;

    // Ensure all previous speech is stopped
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const enVoice = voices.find(v => v.lang.startsWith('en-US')) || voices.find(v => v.lang.startsWith('en'));

    if (enVoice) utterance.voice = enVoice;
    utterance.lang = 'en-US';
    utterance.rate = 0.85;
    utterance.pitch = 1.1;

    utterance.addEventListener('end', () => {
      isSpeaking.current = false;
    });

    utterance.addEventListener('error', (e) => {
      console.error('Speech synthesis error:', e);
      isSpeaking.current = false;
    });

    isSpeaking.current = true;
    window.speechSynthesis.speak(utterance);
  }, []);

  // Main speak function
  const speak = useCallback(async (text: string, voice?: string) => {
    if (!text.trim() || isSpeaking.current) return;

    // Stop any current playback
    stopAllAudio();

    try {
      const audioUrl = await apiClient.textToSpeech(text, voice);

      if (audioUrl) {
        currentAudio.current = new Audio(audioUrl);

        currentAudio.current.addEventListener('ended', () => {
          currentAudio.current = null;
          isSpeaking.current = false;
        });

        currentAudio.current.addEventListener('error', (e) => {
          console.error('Audio playback failed:', e);
          currentAudio.current = null;
          isSpeaking.current = false;
          fallbackSpeak(text);
        });

        isSpeaking.current = true;
        await currentAudio.current.play();
      } else {
        fallbackSpeak(text);
      }
    } catch (error) {
      console.error('TTS Error:', error);
      fallbackSpeak(text);
    }
  }, [stopAllAudio, fallbackSpeak]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllAudio();
    };
  }, [stopAllAudio]);

  return {
    speak,
    stopAllAudio,
    isSpeaking: isSpeaking.current
  };
};